// src/components/SubPayPanel.js
// ---------------------------------------------------------------------------
// "Sub Pay" on a job's details.
//
//   Admin (SUB_PAY_ROLES)  every subcontractor on the job: how they're paid
//                          (Daily / Project), owed, paid, balance, every
//                          payment (date, how, check #). Edit the pay terms,
//                          record / edit / delete payments.
//   The sub themself       only their own pay on this job, read-only.
//   Everyone else          nothing.
//
// Reads and writes the same records as Contractor Pay and the Job Estimate
// Spreadsheet (jobs/{id}.subcontractorPay[] for owed, "subcontractorPay"
// payments with jobId), so the back end and this panel always agree.
// Collapsed by default and reads Firestore only while open.
//
//   <SubPayPanel job={selectedJob} userRole={userRole} currentUser={me}
//     onJobUpdated={(patch) => ...} />
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { collection, doc, getDoc, getDocs, query, serverTimestamp, updateDoc, where } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../firebaseConfig";
import { isSubcontractor } from "../utils/contractorIdentity";
import {
  applySubPayTerms,
  canSeeSubPay,
  computeTermOwed,
  isOwnTerm,
  isRecurringJob,
  isTermStale,
  lineOwed,
  newTerm,
  normalizeSubName,
  round2,
  termKey,
} from "../utils/subPayTerms";
import {
  SUB_PAY_METHODS,
  applyCopyToContinued,
  deleteSubPayment,
  findContinuedJobs,
  planCopyToContinued,
  findProjectJob,
  loadPeople,
  makeSameSub,
  normalizePayment,
  rebalanceDailySplit,
  recordSubPayment,
  todayKey,
  updateSubPayment,
} from "../utils/subPayPayments";
import SubPayTermFields, { fmtMoney } from "./SubPayTermFields";
import { findSubUser, findTerm, guardTermEdit, subDefaultRate } from "./SubPayTermsInline";
import SubPayDirections from "./SubPayDirections";
import "./SubPayPanel.css";
import { useLanguage, TranslatedText } from "../i18n";
import { describeTermFor } from "./describeTermI18n";

const loadPaymentsForJobIds = async (ids) => {
  const list = Array.from(new Set(ids.filter(Boolean)));
  const out = [];
  for (let i = 0; i < list.length; i += 10) {
    const chunk = list.slice(i, i + 10);
    const [a, b] = await Promise.all([
      getDocs(query(collection(db, "subcontractorPay"), where("jobId", "in", chunk))),
      getDocs(query(collection(db, "subcontractorPayments"), where("jobId", "in", chunk))).catch(() => ({ docs: [] })),
    ]);
    a.docs.forEach((d) => out.push(normalizePayment(d, "subcontractorPay")));
    b.docs.forEach((d) => out.push(normalizePayment(d, "subcontractorPayments")));
  }
  return out.filter((p) => !p.occurrenceKey);
};

const lineFor = (job, name, sameSub) =>
  (Array.isArray(job?.subcontractorPay) ? job.subcontractorPay : []).find((l) =>
    sameSub(l?.subcontractorName || l?.name || "", name)
  ) || null;

const EMPTY_PAY = { amount: "", date: todayKey(), method: "Check", reference: "", note: "" };

function PaymentForm({ initial, busy, submitLabel, onSubmit, onCancel }) {
  const { t } = useLanguage();
  const [f, setF] = useState({ ...EMPTY_PAY, ...initial });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  return (
    <div className="spp-payform">
      <label>
        <span>Amount</span>
        <input type="number" min="0" step="0.01" value={f.amount} onChange={set("amount")} />
      </label>
      <label>
        <span>Date paid</span>
        <input type="date" value={f.date} onChange={set("date")} />
      </label>
      <label>
        <span>How</span>
        <select value={f.method} onChange={set("method")}>
          {SUB_PAY_METHODS.concat(SUB_PAY_METHODS.includes(f.method) || !f.method ? [] : [f.method]).map((m) => (
            <option key={m}>{m}</option>
          ))}
        </select>
      </label>
      <label>
        <span>Check # / Ref</span>
        <input value={f.reference} onChange={set("reference")} />
      </label>
      <label className="spp-payform__wide">
        <span>Note</span>
        <input value={f.note} onChange={set("note")} />
      </label>
      <div className="spp-payform__btns">
        <button type="button" className="spp-btn spp-btn--primary" disabled={busy} onClick={() => onSubmit(f)}>
          {busy ? t("Saving…") : submitLabel}
        </button>
        <button type="button" className="spp-btn" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

const SubPayPanel = ({ job, userRole, currentUser = null, onJobUpdated, onChanged, defaultOpen = false }) => {
  const { t: tr, lang } = useLanguage();
  const isAdmin = canSeeSubPay(userRole);
  const isSubViewer = !isAdmin && !!currentUser && isSubcontractor(currentUser);
  const visible = !!job?.id && (isAdmin || isSubViewer);

  const [open, setOpen] = useState(defaultOpen);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fresh, setFresh] = useState(null);
  const [people, setPeople] = useState(null);
  const [payments, setPayments] = useState([]);
  const [roots, setRoots] = useState({}); // bucket -> { id, wo, job }
  const [children, setChildren] = useState([]); // jobs continued from this one
  const [extraSubs, setExtraSubs] = useState([]); // subs added here who aren't on the job yet
  const [drafts, setDrafts] = useState({}); // bucket -> term being edited
  const [payFor, setPayFor] = useState(""); // bucket with the Record payment form open
  const [editPay, setEditPay] = useState(""); // payment id being edited
  const [busy, setBusy] = useState("");
  const [showHelp, setShowHelp] = useState(false);

  const sameSub = useMemo(() => makeSameSub(people), [people]);
  const bucketOf = useCallback(
    (name) => (people ? people.bucketKeyForName(name) : `n:${normalizeSubName(name)}`),
    [people]
  );

  const load = useCallback(async () => {
    if (!job?.id) return;
    setLoading(true);
    setError("");
    try {
      const [snap, ppl] = await Promise.all([getDoc(doc(db, "jobs", job.id)), loadPeople()]);
      const current = snap.exists() ? { id: snap.id, ...snap.data() } : { ...job };
      const bucket = (n) => ppl.bucketKeyForName(n);

      // Where a rolled-over project price is owed.
      const rootMap = {};
      for (const term of Object.values(current.subPayTerms || {})) {
        if (term?.name && term.mode === "project" && !isOwnTerm(current, term)) {
          const r = await findProjectJob(current, term).catch(() => null);
          if (r) rootMap[bucket(term.name)] = r;
        }
      }
      const pays = await loadPaymentsForJobIds([current.id, ...Object.values(rootMap).map((r) => r.id)]);
      setPeople(ppl);
      setFresh(current);
      setRoots(rootMap);
      if (canSeeSubPay(userRole)) {
        setChildren(await findContinuedJobs(current.id).catch(() => []));
      }
      setPayments(pays);
    } catch (err) {
      console.error("Sub Pay: could not load", err);
      setError(tr("Sub pay could not load. Check your connection and try again."));
    } finally {
      setLoading(false);
    }
  }, [job, userRole]);

  useEffect(() => {
    if (open && visible) load();
  }, [open, visible, load]);

  const theJob = fresh || job;

  // One group per contractor on this job, from terms, owed lines, assigned
  // subs and payments.
  const groups = useMemo(() => {
    if (!theJob) return [];
    const map = new Map();
    const add = (name, source) => {
      const n = String(name || "").trim();
      if (!n) return;
      const key = bucketOf(n);
      if (!key) return;
      if (!map.has(key)) {
        map.set(key, {
          key,
          name: n,
          displayName: people ? people.displayNameFor(n) : n,
          identity: people ? people.resolveIdentity(n) : null,
          sources: new Set(),
        });
      }
      map.get(key).sources.add(source);
    };
    Object.values(theJob.subPayTerms || {}).forEach((t) => add(t?.name, "term"));
    (theJob.subcontractorPay || []).forEach((l) => add(l?.subcontractorName || l?.name, "line"));
    const techs = Array.isArray(theJob.techAssign) ? theJob.techAssign : theJob.techAssign ? [theJob.techAssign] : [];
    techs.forEach((t) => {
      if (people && findSubUser(t, people.staff)) add(t, "assigned");
    });
    payments.filter((p) => p.jobId === theJob.id).forEach((p) => add(p.subcontractorName, "payment"));
    extraSubs.forEach((n) => add(n, "added"));

    return Array.from(map.values()).map((g) => {
      // Prefer the techAssign / term name for display of terms
      const term = Object.values(theJob.subPayTerms || {}).find((t) => t?.name && bucketOf(t.name) === g.key) || null;
      const name = term?.name || g.name;
      const line = lineFor(theJob, name, sameSub);
      const owedHere = line ? lineOwed(line) : 0;
      const paysHere = payments.filter((p) => p.jobId === theJob.id && bucketOf(p.subcontractorName) === g.key);
      const paidHere = round2(paysHere.reduce((s, p) => s + p.amount, 0));
      const calc = term ? computeTermOwed(theJob, term) : null;
      const root = term && calc && calc.mode === "project" && !calc.own ? roots[g.key] || null : null;
      let rootOwed = 0;
      let rootPaid = 0;
      let rootPays = [];
      if (root) {
        const rl = lineFor(root.job, name, sameSub);
        rootOwed = rl ? lineOwed(rl) : 0;
        rootPays = payments.filter((p) => p.jobId === root.id && bucketOf(p.subcontractorName) === g.key);
        rootPaid = round2(rootPays.reduce((s, p) => s + p.amount, 0));
      }
      const stale = term ? isTermStale(theJob, term) : false;
      const backEndEdited =
        term && !stale && line && Math.abs(Number(line.basePay || 0) - Number(term.computedAmount || 0)) > 0.004;
      const lineRemoved = Boolean(term && !stale && !line && Number(term.computedAmount || 0) > 0);
      const subUser = people ? findSubUser(name, people.staff) : null;
      return {
        ...g,
        name,
        term,
        calc,
        line,
        owedHere,
        paysHere,
        paidHere,
        balanceHere: round2(owedHere - paidHere),
        root,
        rootOwed,
        rootPaid,
        rootPays,
        stale,
        backEndEdited,
        lineRemoved,
        subUser,
        isSub: Boolean(subUser || g.identity),
      };
    });
  }, [theJob, people, payments, roots, bucketOf, sameSub, extraSubs]);

  // Every sub contractor on file, for "+ Add a sub contractor" (clean-up of
  // old jobs where the sub was never put on Tech Assign).
  const subChoices = useMemo(() => {
    if (!people) return [];
    const onJob = new Set(groups.map((g) => g.key));
    return people.identities
      .filter((i) => !onJob.has(i.key))
      .map((i) => {
        const personName = i.staff ? `${i.staff.firstName || ""} ${i.staff.lastName || ""}`.trim() : "";
        return { key: i.key, name: personName || i.displayName, label: i.displayName + (personName && personName !== i.displayName ? ` (${personName})` : "") };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [people, groups]);

  const myKey = useMemo(() => {
    if (!isSubViewer || !people || !currentUser) return "";
    const identity = people.identities.find((i) => i.ids.has(currentUser.id));
    if (identity) return identity.key;
    return bucketOf(`${currentUser.firstName || ""} ${currentUser.lastName || ""}`.trim());
  }, [isSubViewer, people, currentUser, bucketOf]);

  const shown = isAdmin ? groups : groups.filter((g) => g.key === myKey);

  const totals = useMemo(() => {
    const owed = round2(shown.reduce((s, g) => s + g.owedHere, 0));
    const paid = round2(shown.reduce((s, g) => s + g.paidHere, 0));
    return { owed, paid, balance: round2(owed - paid) };
  }, [shown]);

  if (!visible) return null;
  // Techs who aren't subs, and subs not on this job, see nothing.
  if (isSubViewer && open && !loading && people && !shown.length) {
    return null;
  }

  /* ---------------- writes ---------------- */
  const editorName = () => {
    const u = getAuth()?.currentUser;
    return u?.displayName || u?.email || "";
  };

  const writeTerms = async (mutate, { force = false } = {}) => {
    setBusy("Saving…");
    try {
      const ref = doc(db, "jobs", job.id);
      const snap = await getDoc(ref);
      const current = { id: snap.id, ...snap.data() };
      const nextTerms = mutate({ ...(current.subPayTerms || {}) }, current);
      if (!nextTerms) return;
      const result = applySubPayTerms({ ...current, subPayTerms: nextTerms }, { sameSub, force });
      let patch = { subPayTerms: result.subPayTerms, subcontractorPay: result.subcontractorPay };
      await updateDoc(ref, { ...patch, subPayTermsUpdatedAt: serverTimestamp() });
      // Same-day split: a Daily sub on other jobs the same day gets one day's
      // pay divided across them. Re-split every sub that is (or was) Daily here.
      const dailyNames = [...Object.values(current.subPayTerms || {}), ...Object.values(result.subPayTerms || {})]
        .filter((t) => t?.name && t.mode !== "project")
        .map((t) => t.name);
      if (dailyNames.length) {
        try {
          const split = await rebalanceDailySplit(dailyNames);
          if (split.patches[job.id]) patch = split.patches[job.id];
        } catch (err) {
          console.warn("Daily split not updated:", err);
        }
      }
      if (typeof onJobUpdated === "function") onJobUpdated(patch);
      await load();
      if (typeof onChanged === "function") onChanged();
    } catch (err) {
      console.error(err);
      alert(`Could not save sub pay.\n\n${err?.message || err}`);
    } finally {
      setBusy("");
    }
  };

  const saveTerm = (g, edited) =>
    writeTerms((terms, current) => {
      const prev = findTerm(terms, g.name);
      const term = guardTermEdit(current, prev, edited);
      if (!term) return null;
      Object.keys(terms).forEach((k) => {
        if (bucketOf(terms[k]?.name) === g.key) delete terms[k];
      });
      terms[termKey(g.name)] = { ...term, name: g.name, updatedAt: new Date().toISOString(), updatedBy: editorName() };
      return terms;
    }).then(() => setDrafts((d) => ({ ...d, [g.key]: undefined })));

  const applyTermsAmount = (g) =>
    writeTerms((terms) => {
      Object.keys(terms).forEach((k) => {
        if (bucketOf(terms[k]?.name) === g.key) terms[k] = { ...terms[k], computedSig: "" };
      });
      return terms;
    });

  const copyToContinued = async (g) => {
    if (!children.length) return;
    const plan = planCopyToContinued({ rootJob: theJob, term: g.term, children, sameSub });
    const lines = plan
      .map((r) => `WO ${r.job.wo || "—"} (${String(r.job.startDate || "").slice(0, 10)}): ${fmtMoney(r.before)} → ${fmtMoney(r.after)}`)
      .join("\n");
    const what =
      g.calc.mode === "project"
        ? "Project: each continued job shows the price, but it is owed once, here on this job."
        : "Daily: same crew and rate on each continued job, using that job's own days. A day shared with another job is split between them.";
    if (!window.confirm(`Copy ${g.displayName}'s pay to ${plan.length} continued job(s)?\n\n${what}\n\nOwed on each job:\n${lines}`)) return;
    setBusy("Copying…");
    try {
      await applyCopyToContinued(plan);
      if (g.calc.mode !== "project") {
        await rebalanceDailySplit([g.term?.name || g.name]).catch((err) => console.warn("Daily split not updated:", err));
      }
      await load();
      if (typeof onChanged === "function") onChanged();
      alert(`Done. ${plan.length} continued job(s) updated.`);
    } catch (err) {
      alert(`Could not copy.\n\n${err?.message || err}`);
    } finally {
      setBusy("");
    }
  };

  const removeTerm = (g) => {
    if (!window.confirm(`Remove ${g.displayName}'s daily / project pay from this job?\n\nThe owed amount it added is taken off too (unless it was changed by hand). Payments are not touched.`)) return;
    setBusy("Saving…");
    (async () => {
      try {
        const ref = doc(db, "jobs", job.id);
        const snap = await getDoc(ref);
        const current = snap.data() || {};
        const terms = { ...(current.subPayTerms || {}) };
        const t = Object.values(terms).find((x) => bucketOf(x?.name) === g.key);
        Object.keys(terms).forEach((k) => {
          if (bucketOf(terms[k]?.name) === g.key) delete terms[k];
        });
        const lines = (current.subcontractorPay || []).filter((l) => {
          if (bucketOf(l?.subcontractorName || "") !== g.key) return true;
          const cos = Array.isArray(l.changeOrders) ? l.changeOrders : [];
          return cos.length > 0 || !t || Number(l.basePay) !== Number(t.computedAmount);
        });
        const patch = { subPayTerms: terms, subcontractorPay: lines };
        await updateDoc(ref, { ...patch, subPayTermsUpdatedAt: serverTimestamp() });
        if (typeof onJobUpdated === "function") onJobUpdated(patch);
        // The sub's other jobs that shared a day with this one get their full share back.
        if (t?.name && t.mode !== "project") {
          await rebalanceDailySplit([t.name]).catch((err) => console.warn("Daily split not updated:", err));
        }
        await load();
        if (typeof onChanged === "function") onChanged();
      } catch (err) {
        alert(`Could not remove.\n\n${err?.message || err}`);
      } finally {
        setBusy("");
      }
    })();
  };

  const submitPayment = async (g, form) => {
    setBusy("Saving payment…");
    try {
      const target = g.root ? g.root.job : theJob;
      await recordSubPayment({
        person: { id: g.subUser?.id || (g.identity?.staff?.id ?? ""), name: g.displayName || g.name },
        job: target,
        ...form,
      });
      setPayFor("");
      await load();
      if (typeof onChanged === "function") onChanged();
    } catch (err) {
      alert(err?.message || "The payment did not save.");
    } finally {
      setBusy("");
    }
  };

  const saveEditedPayment = async (p, form) => {
    setBusy("Saving payment…");
    try {
      await updateSubPayment(p, form);
      setEditPay("");
      await load();
      if (typeof onChanged === "function") onChanged();
    } catch (err) {
      alert(err?.message || "The payment did not save.");
    } finally {
      setBusy("");
    }
  };

  const removePayment = async (p) => {
    if (!window.confirm(`Delete this ${fmtMoney(p.amount)} payment from ${p.date}? It is removed everywhere (Contractor Pay, Job Estimate Spreadsheet, dashboards).`)) return;
    setBusy("Deleting…");
    try {
      await deleteSubPayment(p);
      await load();
      if (typeof onChanged === "function") onChanged();
    } catch (err) {
      alert(`Could not delete.\n\n${err?.message || err}`);
    } finally {
      setBusy("");
    }
  };

  /* ---------------- render ---------------- */
  const renderPayments = (list, canEdit) =>
    list.length ? (
      <table className="spp-table">
        <thead>
          <tr>
            <th>{tr("Date")}</th>
            <th>{tr("Amount")}</th>
            <th>{tr("How")}</th>
            <th>{tr("Check # / Ref")}</th>
            <th>{tr("Note")}</th>
            {canEdit && <th />}
          </tr>
        </thead>
        <tbody>
          {list
            .slice()
            .sort((a, b) => String(b.date).localeCompare(String(a.date)))
            .map((p) =>
              editPay === p.id ? (
                <tr key={p.id}>
                  <td colSpan={canEdit ? 6 : 5}>
                    <PaymentForm
                      initial={{ amount: p.amount, date: p.date, method: p.method, reference: p.reference, note: p.note }}
                      busy={!!busy}
                      submitLabel="Save payment"
                      onSubmit={(f) => saveEditedPayment(p, f)}
                      onCancel={() => setEditPay("")}
                    />
                  </td>
                </tr>
              ) : (
                <tr key={p.id}>
                  <td>{p.date}</td>
                  <td>{fmtMoney(p.amount)}</td>
                  <td>{p.method ? tr(p.method) : "—"}</td>
                  <td>{p.reference || "—"}</td>
                  <td>{p.note ? <TranslatedText text={p.note} hint={p.lang} /> : ""}</td>
                  {canEdit && (
                    <td className="spp-table__actions">
                      <button type="button" className="spp-link" onClick={() => setEditPay(p.id)}>
                        Edit
                      </button>
                      <button type="button" className="spp-link spp-link--danger" onClick={() => removePayment(p)}>
                        Delete
                      </button>
                    </td>
                  )}
                </tr>
              )
            )}
        </tbody>
      </table>
    ) : (
      <div className="spp-muted">{tr("No payments yet.")}</div>
    );

  return (
    <div className="spp-panel">
      <button type="button" className="spp-panel__toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span>
          {open ? "▾" : "▸"} {isAdmin ? "Sub Pay" : tr("My pay on this job")}
        </span>
        {open && fresh && (
          <span className="spp-panel__sum">
            {tr("Owed")} {fmtMoney(totals.owed)} · {tr("Paid")} {fmtMoney(totals.paid)} · {tr("Balance")} <b>{fmtMoney(totals.balance)}</b>
          </span>
        )}
        {isAdmin && <span className="spp-badge">Admin only</span>}
      </button>

      {open && (
        <div className="spp-panel__body">
          <div className="spp-panel__bar">
            {/* Directions explain the office's pay setup; subs don't need them. */}
            {isAdmin && (
              <button type="button" className="spp-link" onClick={() => setShowHelp(true)}>
                {tr("Directions")}
              </button>
            )}
            <button type="button" className="spp-link" onClick={load} disabled={loading}>
              ↻ {tr("Refresh")}
            </button>
            {busy && <span className="spp-muted">{busy}</span>}
          </div>

          {loading && !fresh && <div className="spp-muted">{tr("Loading…")}</div>}
          {error && <div className="spp-warn">{error}</div>}
          {isAdmin && fresh && !isRecurringJob(fresh) && subChoices.length > 0 && (
            <div className="spp-sub__terms">
              <span className="spp-muted">+ Add a sub contractor to this job's pay:</span>
              <select
                value=""
                onChange={(e) => {
                  const pick = subChoices.find((c) => c.key === e.target.value);
                  if (!pick) return;
                  setExtraSubs((list) => [...list, pick.name]);
                  setDrafts((d) => ({ ...d, [pick.key]: newTerm(pick.name, { mode: "daily", jobId: theJob.id }) }));
                }}
                style={{ width: "auto", minWidth: 200 }}
              >
                <option value="">Choose…</option>
                {subChoices.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {fresh && !shown.length && (
            <div className="spp-muted">
              {tr("No sub contractors on this job yet. Add one under Tech Assign (Edit), then pick Daily or Project.")}
            </div>
          )}
          {fresh && isRecurringJob(fresh) && isAdmin && (
            <div className="spp-note">
              Recurring job: daily / project pay isn't used here. Sub pay per visit is on the Job Estimate
              Spreadsheet.
            </div>
          )}

          {shown.map((g) => {
            const draft = drafts[g.key];
            const canEdit = isAdmin;
            return (
              <div key={g.key} className="spp-sub">
                <div className="spp-sub__head">
                  <strong>{g.displayName}</strong>
                  {g.displayName !== g.name && <span className="spp-muted">({g.name})</span>}
                  {g.term && <span className="spp-chip spp-chip--on">{g.calc.mode === "project" ? tr("Project") : tr("Daily")}</span>}
                  {!g.term && g.line && <span className="spp-chip">{tr("Set on back end")}</span>}
                </div>

                {/* Pay terms */}
                {g.term && !draft && (
                  <div className="spp-sub__terms">
                    <span>{describeTermFor(lang, theJob, g.term, fmtMoney)}</span>
                    {canEdit && (
                      <>
                        <button type="button" className="spp-link" onClick={() => setDrafts((d) => ({ ...d, [g.key]: { ...g.term } }))}>
                          Edit pay
                        </button>
                        <button type="button" className="spp-link spp-link--danger" onClick={() => removeTerm(g)}>
                          Remove
                        </button>
                        {g.calc?.own && children.length > 0 && (
                          <button type="button" className="spp-link" onClick={() => copyToContinued(g)}>
                            Copy to continued jobs ({children.length})
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
                {canEdit && !g.term && g.isSub && !isRecurringJob(theJob) && !draft && (
                  <div className="spp-sub__terms">
                    <span className="spp-muted">Paid by:</span>
                    <button type="button" className="spp-chip" onClick={() => setDrafts((d) => ({ ...d, [g.key]: newTerm(g.name, { mode: "daily", rate: subDefaultRate(g.subUser, theJob.startDate), jobId: theJob.id }) }))}>
                      Daily
                    </button>
                    <button type="button" className="spp-chip" onClick={() => setDrafts((d) => ({ ...d, [g.key]: newTerm(g.name, { mode: "project", rate: subDefaultRate(g.subUser, theJob.startDate), jobId: theJob.id }) }))}>
                      Project
                    </button>
                    {g.line && <span className="spp-muted">Setting this replaces the back-end amount.</span>}
                  </div>
                )}
                {canEdit && draft && (
                  <div className="spp-sub__edit">
                    <SubPayTermFields
                      job={theJob}
                      term={draft}
                      projectHeldOn={g.root?.wo || ""}
                      onChange={(t) => setDrafts((d) => ({ ...d, [g.key]: t }))}
                    />
                    <div className="spp-payform__btns">
                      <button type="button" className="spp-btn spp-btn--primary" disabled={!!busy} onClick={() => saveTerm(g, draft)}>
                        Save pay
                      </button>
                      <button type="button" className="spp-btn" onClick={() => setDrafts((d) => ({ ...d, [g.key]: undefined }))}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {g.stale && canEdit && (
                  <div className="spp-warn">
                    The job's dates or pay changed since the owed amount was worked out.{" "}
                    <button type="button" className="spp-link" onClick={() => applyTermsAmount(g)}>
                      Update owed to {fmtMoney(g.calc.amount)}
                    </button>
                  </div>
                )}
                {g.backEndEdited && canEdit && (
                  <div className="spp-note">
                    Owed was changed on the back end to <b>{fmtMoney(g.line.basePay)}</b> (the pay terms work out to{" "}
                    {fmtMoney(g.term.computedAmount)}). The back-end amount is kept.{" "}
                    <button type="button" className="spp-link" onClick={() => applyTermsAmount(g)}>
                      Use terms amount
                    </button>
                  </div>
                )}

                {g.lineRemoved && canEdit && (
                  <div className="spp-note">
                    The owed amount for this sub was removed on the back end. Nothing is owed on this job
                    right now.{" "}
                    <button type="button" className="spp-link" onClick={() => applyTermsAmount(g)}>
                      Add back {fmtMoney(g.calc.amount)}
                    </button>
                  </div>
                )}

                {/* Money on THIS job */}
                <div className="spp-stats">
                  <div>
                    <span>{tr("Owed on this job")}</span>
                    <b>{fmtMoney(g.owedHere)}</b>
                    {g.line && (g.line.changeOrders || []).length > 0 && (
                      <small>{tr("incl. change orders {amount}", { amount: fmtMoney(lineOwed(g.line) - Number(g.line.basePay || 0)) })}</small>
                    )}
                  </div>
                  <div>
                    <span>{tr("Paid")}</span>
                    <b>{fmtMoney(g.paidHere)}</b>
                  </div>
                  <div>
                    <span>{tr("Balance")}</span>
                    <b className={g.balanceHere > 0.004 ? "spp-owing" : g.balanceHere < -0.004 ? "spp-over" : "spp-clear"}>
                      {fmtMoney(g.balanceHere)}
                    </b>
                  </div>
                </div>

                {/* Project price that rolled over from an earlier WO */}
                {g.root && (
                  <div className="spp-root">
                    <div className="spp-root__title">
                      {tr("Project price is owed on")} <b>WO {g.root.wo || tr("(first job)")}</b>
                    </div>
                    <div className="spp-stats">
                      <div>
                        <span>{tr("Project owed")}</span>
                        <b>{fmtMoney(g.rootOwed)}</b>
                      </div>
                      <div>
                        <span>{tr("Paid")}</span>
                        <b>{fmtMoney(g.rootPaid)}</b>
                      </div>
                      <div>
                        <span>{tr("Balance")}</span>
                        <b className={g.rootOwed - g.rootPaid > 0.004 ? "spp-owing" : "spp-clear"}>
                          {fmtMoney(round2(g.rootOwed - g.rootPaid))}
                        </b>
                      </div>
                    </div>
                    {renderPayments(g.rootPays, canEdit)}
                  </div>
                )}

                {(!g.root || g.paysHere.length > 0) && renderPayments(g.paysHere, canEdit)}

                {canEdit &&
                  (payFor === g.key ? (
                    <PaymentForm
                      initial={{
                        amount: Math.max(0, g.root ? round2(g.rootOwed - g.rootPaid) : g.balanceHere) || "",
                      }}
                      busy={!!busy}
                      submitLabel={g.root ? `Record payment (WO ${g.root.wo})` : "Record payment"}
                      onSubmit={(f) => submitPayment(g, f)}
                      onCancel={() => setPayFor("")}
                    />
                  ) : (
                    <button type="button" className="spp-btn spp-btn--primary" onClick={() => setPayFor(g.key)}>
                      + Record payment
                    </button>
                  ))}
              </div>
            );
          })}
        </div>
      )}
      {isAdmin && <SubPayDirections open={showHelp} onClose={() => setShowHelp(false)} />}
    </div>
  );
};

export default SubPayPanel;
